import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Minus, Plus, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { apiClient } from "../../App";

const THEMES = {
  day:   { id: "day",   bg: "#FFFDF8", fg: "#1F2937", muted: "#6B7280", line: "#E7E2D6", hl: "#D1FAE5", accent: "#0D9488" },
  night: { id: "night", bg: "#0B1220", fg: "#E5E7EB", muted: "#94A3B8", line: "#1E293B", hl: "#12304A", accent: "#2DD4BF" },
};

const MEASURE_FS = 20;
const MIN_FS = 16;
const MAX_FS = 56;

const toArabicDigits = (n) => String(n).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);

const readPref = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
};

// Page data never changes, so keep it across remounts
const pageCache = new Map();
// Set when the user jumps to another surah so the new view starts at the top
let pendingScrollToTop = false;

const renderWords = (words, t, activeKey, measure) =>
  words.map((w, i) =>
    w.end ? (
      <span
        key={i}
        style={{
          flex: "0 0 auto",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "1.35em",
          height: "1.35em",
          borderRadius: "9999px",
          border: `1.5px solid ${t.accent}`,
          color: t.accent,
          fontSize: "0.6em",
          lineHeight: 1,
        }}
      >
        {toArabicDigits(w.v)}
      </span>
    ) : (
      <span
        key={i}
        {...(measure ? {} : { "data-vk": w.vk })}
        style={{
          flex: "0 0 auto",
          borderRadius: 6,
          backgroundColor: !measure && activeKey && w.vk === activeKey ? t.hl : "transparent",
        }}
      >
        {w.t}
      </span>
    )
  );

const MushafPage = ({ page, t, activeKey, isEnglish, eager, size, onFit }) => {
  const [data, setData] = useState(() => pageCache.get(page) || null);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [near, setNear] = useState(!!eager || pageCache.has(page));
  const [meas, setMeas] = useState(null);
  const holderRef = useRef(null);
  const wrapRef = useRef(null);
  const measRef = useRef(null);

  // Load a page only when it is close to the screen
  useEffect(() => {
    if (near) return;
    const el = holderRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "900px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near || data) return;
    let cancelled = false;
    setError(false);
    apiClient
      .get(`/mushaf/page/${page}`)
      .then((r) => {
        pageCache.set(page, r.data);
        if (!cancelled) setData(r.data);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [near, data, page, retryKey]);

  // Measure each line at a fixed size in a hidden copy, independent of the chosen font size
  useLayoutEffect(() => {
    const el = wrapRef.current;
    const mEl = measRef.current;
    if (!el || !mEl || !data) return;
    const run = () => {
      const W = el.clientWidth - 8;
      if (W <= 0) return;
      const widths = {};
      mEl.querySelectorAll("[data-line]").forEach((row) => {
        const kids = Array.from(row.children);
        const sum = kids.reduce((s, c) => s + c.getBoundingClientRect().width, 0);
        widths[row.getAttribute("data-line")] =
          Math.round((sum + Math.max(0, kids.length - 1) * 0.3 * MEASURE_FS) * 10) / 10;
      });
      setMeas((prev) => {
        if (prev && Math.abs(prev.W - W) < 1 && JSON.stringify(prev.widths) === JSON.stringify(widths)) return prev;
        return { W, widths };
      });
    };
    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    return () => ro.disconnect();
  }, [data]);

  const maxW = meas ? Math.max(1, ...Object.values(meas.widths)) : 0;
  const measured = !!meas && maxW > 1;
  const fit = measured
    ? Math.max(12, Math.min(MEASURE_FS * (meas.W / maxW) * 0.985, meas.W / 11))
    : 22;

  useEffect(() => {
    if (measured) onFit(fit);
  }, [measured, fit, onFit]);

  const eff = size != null ? size : fit;
  const wrapMode = size != null && measured && eff > fit * 1.02;
  const rowH = eff * 2.15;
  const boxH = data ? (wrapMode ? undefined : rowH * 15) : 640;

  const renderHeader = (ln) =>
    ln.type === "surah_name" ? (
      <div key={`h${ln.line}`} style={{ height: rowH, display: "flex", alignItems: "center" }}>
        <div
          style={{
            width: "100%",
            textAlign: "center",
            border: `2px solid ${t.accent}`,
            borderRadius: 6,
            background: t.hl,
            fontSize: eff * 0.9,
            lineHeight: 1.5,
          }}
        >
          {ln.text}
        </div>
      </div>
    ) : (
      <div
        key={`h${ln.line}`}
        dir="rtl"
        style={{ height: rowH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: eff }}
      >
        {ln.text}
      </div>
    );

  let body = null;
  if (data && !wrapMode) {
    body = data.lines.map((ln) => {
      if (ln.type !== "ayah") return renderHeader(ln);
      const short = measured && (meas.widths[ln.line] || 0) * (eff / MEASURE_FS) < meas.W * 0.62;
      return (
        <div
          key={ln.line}
          dir="rtl"
          style={{
            height: rowH,
            display: "flex",
            alignItems: "center",
            justifyContent: short ? "center" : "space-between",
            gap: "0.3em",
            whiteSpace: "nowrap",
            fontSize: eff,
          }}
        >
          {renderWords(ln.words, t, activeKey, false)}
        </div>
      );
    });
  } else if (data && wrapMode) {
    const segments = [];
    let buf = null;
    data.lines.forEach((ln) => {
      if (ln.type === "ayah") {
        if (!buf) {
          buf = { type: "ayah", line: ln.line, words: [] };
          segments.push(buf);
        }
        buf.words.push(...ln.words);
      } else {
        buf = null;
        segments.push(ln);
      }
    });
    body = segments.map((seg) =>
      seg.type === "ayah" ? (
        <div
          key={`a${seg.line}`}
          dir="rtl"
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-start",
            columnGap: "0.3em",
            rowGap: "0.25em",
            lineHeight: 2.2,
            fontSize: eff,
          }}
        >
          {renderWords(seg.words, t, activeKey, false)}
        </div>
      ) : (
        renderHeader(seg)
      )
    );
  }

  return (
    <section ref={holderRef} className="rounded-xl border mb-4 px-3 pt-3 pb-3" style={{ borderColor: t.line }}>
      <div className="flex items-center justify-between text-xs mb-2 px-1" style={{ color: t.muted }}>
        <span>{data?.surah_name ? `${isEnglish ? "Surah" : "Surat"} ${data.surah_name}` : ""}</span>
        <span>{data?.juz ? `Juz' ${data.juz}` : ""}</span>
      </div>

      <div ref={wrapRef} style={{ minHeight: boxH, position: "relative" }}>
        {!data && !error && (
          <div className="flex items-center justify-center" style={{ height: 640 }}>
            {near && <Loader2 className="w-6 h-6 animate-spin" style={{ color: t.muted }} />}
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center justify-center gap-3 text-sm" style={{ height: 640, color: t.muted }}>
            <span>{isEnglish ? "Failed to load this page" : "Gagal memuat halaman ini"}</span>
            <button
              type="button"
              onClick={() => setRetryKey((k) => k + 1)}
              className="px-3 py-1.5 rounded-lg border text-xs"
              style={{ borderColor: t.line, color: t.fg }}
            >
              {isEnglish ? "Retry" : "Coba lagi"}
            </button>
          </div>
        )}

        {data && (
          <div
            ref={measRef}
            aria-hidden="true"
            className="font-uthmani"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: 0,
              overflow: "hidden",
              visibility: "hidden",
              pointerEvents: "none",
              fontSize: MEASURE_FS,
            }}
          >
            {data.lines
              .filter((ln) => ln.type === "ayah")
              .map((ln) => (
                <div
                  key={ln.line}
                  data-line={ln.line}
                  dir="rtl"
                  style={{ display: "flex", gap: "0.3em", whiteSpace: "nowrap", width: "max-content" }}
                >
                  {renderWords(ln.words, t, null, true)}
                </div>
              ))}
          </div>
        )}

        {data && (
          <div
            className="font-uthmani"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: boxH,
            }}
          >
            {body}
          </div>
        )}
      </div>

      <div className="text-center text-xs mt-3" style={{ color: t.muted }}>
        {toArabicDigits(page)}
      </div>
    </section>
  );
};

const SurahNav = ({ info, t, isEnglish, onGo }) => {
  const item = (target, dir) => {
    const label =
      dir === "prev"
        ? (isEnglish ? "Previous surah" : "Surat sebelumnya")
        : (isEnglish ? "Next surah" : "Surat berikutnya");
    const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
    return (
      <button
        type="button"
        disabled={!target}
        onClick={() => target && onGo(target.id)}
        className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-xl border disabled:opacity-30 ${
          dir === "next" ? "flex-row-reverse text-right" : "text-left"
        }`}
        style={{ borderColor: t.line, color: t.fg }}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="min-w-0">
          <span className="block text-[11px]" style={{ color: t.muted }}>{label}</span>
          <span className="block text-sm font-semibold truncate">{target?.name || "-"}</span>
        </span>
      </button>
    );
  };
  return (
    <div className="flex gap-2">
      {item(info?.prev, "prev")}
      {item(info?.next, "next")}
    </div>
  );
};

const ReadingMode = ({ surahNumber, currentAyah, isEnglish }) => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => (readPref("reading-theme", "day") === "night" ? "night" : "day"));
  const [size, setSize] = useState(() => {
    const n = parseInt(readPref("reading-fs", "auto"), 10);
    return Number.isFinite(n) ? Math.min(MAX_FS, Math.max(MIN_FS, n)) : null;
  });
  const [info, setInfo] = useState(null);
  const topRef = useRef(null);
  const fitRef = useRef(24);
  const t = THEMES[theme] || THEMES.day;

  const onFit = useCallback((f) => { fitRef.current = f; }, []);
  const bump = (d) =>
    setSize((s) => {
      const base = s != null ? s : Math.round(fitRef.current);
      return Math.min(MAX_FS, Math.max(MIN_FS, base + d));
    });

  useEffect(() => {
    try { localStorage.setItem("reading-theme", theme); } catch {}
  }, [theme]);
  useEffect(() => {
    try { localStorage.setItem("reading-fs", size == null ? "auto" : String(size)); } catch {}
  }, [size]);

  useEffect(() => {
    if (pendingScrollToTop) {
      pendingScrollToTop = false;
      if (topRef.current) topRef.current.scrollIntoView({ block: "start" });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    apiClient
      .get(`/mushaf/surah-start/${surahNumber}`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => {
        if (cancelled) return;
        setInfo({
          page: 1,
          end_page: 1,
          prev: surahNumber > 1 ? { id: surahNumber - 1, name: `#${surahNumber - 1}` } : null,
          next: surahNumber < 114 ? { id: surahNumber + 1, name: `#${surahNumber + 1}` } : null,
        });
      });
    return () => { cancelled = true; };
  }, [surahNumber]);

  const goSurah = useCallback((n) => {
    pendingScrollToTop = true;
    setTimeout(() => { pendingScrollToTop = false; }, 3000);
    navigate(`/quran/${n}`);
  }, [navigate]);

  // Scroll to the verse being played, when its page is already on screen
  useEffect(() => {
    if (!currentAyah) return;
    const el = document.querySelector(`[data-vk="${surahNumber}:${currentAyah}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentAyah, surahNumber]);

  const activeKey = currentAyah ? `${surahNumber}:${currentAyah}` : null;
  const last = info ? (info.end_page || info.page) : 0;
  const pages = info ? Array.from({ length: Math.max(1, last - info.page + 1) }, (_, i) => info.page + i) : [];

  const isDay = theme === "day";
  const ThemeIcon = isDay ? Moon : Sun;
  const themeLabel = isDay ? (isEnglish ? "Night" : "Malam") : (isEnglish ? "Day" : "Siang");
  const iconBtn = "w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40";
  const barStyle = { borderColor: t.line, color: t.fg };

  return (
    <div
      ref={topRef}
      className="rounded-2xl border p-3 max-w-[640px] mx-auto transition-colors duration-300"
      style={{ backgroundColor: t.bg, color: t.fg, borderColor: t.line }}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          onClick={() => setTheme(isDay ? "night" : "day")}
          className="flex items-center gap-1.5 px-3 h-8 rounded-lg border text-xs font-medium"
          style={barStyle}
          aria-label={themeLabel}
        >
          <ThemeIcon className="w-4 h-4" />
          {themeLabel}
        </button>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => bump(-2)}
            disabled={size != null && size <= MIN_FS}
            className={iconBtn}
            style={barStyle}
            aria-label={isEnglish ? "Smaller text" : "Perkecil huruf"}
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setSize(null)}
            className="min-w-[3.25rem] h-8 px-2 rounded-lg border text-xs font-medium"
            style={{ ...barStyle, backgroundColor: size == null ? t.hl : "transparent" }}
            title={isEnglish ? "Reset to automatic fit" : "Kembali ke ukuran otomatis"}
          >
            {size == null ? "Auto" : size}
          </button>
          <button
            type="button"
            onClick={() => bump(2)}
            disabled={size != null && size >= MAX_FS}
            className={iconBtn}
            style={barStyle}
            aria-label={isEnglish ? "Larger text" : "Perbesar huruf"}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mb-3">
        <SurahNav info={info} t={t} isEnglish={isEnglish} onGo={goSurah} />
      </div>

      {!info ? (
        <div className="flex items-center justify-center" style={{ height: 320 }}>
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: t.muted }} />
        </div>
      ) : (
        pages.map((p, i) => (
          <MushafPage
            key={p}
            page={p}
            t={t}
            activeKey={activeKey}
            isEnglish={isEnglish}
            eager={i < 2}
            size={size}
            onFit={onFit}
          />
        ))
      )}

      {info && (
        <div className="mt-1">
          <SurahNav info={info} t={t} isEnglish={isEnglish} onGo={goSurah} />
        </div>
      )}
    </div>
  );
};

export default ReadingMode;
